import React, { useState } from "react";
import { Badge } from "/src/modern/components/ui/badge";
import { Button } from "/src/modern/components/ui/button";
import { Card, CardContent } from "/src/modern/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "/src/modern/components/ui/avatar";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "/src/modern/components/ui/tooltip";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "/src/modern/components/ui/dropdown-menu";
import { useBoardCardActions } from "../../hooks/useBoardCardActions.jsx";

export function WorkspaceBoardCard({ board, onWorkforceActiveChange, onBoardAccess}) {
  if (!board) {
    return null;
  }

  const {
    name,
    title,
    memberCount,
    adminEmails,
    lastUpdatedAt,
    updatedBy,
    age,
    role,
    workforceActive,
    isWorkforceActive: boardWorkforceActive,
  } = board;
  // Use title if name is not available (for compatibility with new data structure)
  const displayName = name || title || 'Untitled Board';
  const isActive = workforceActive ? workforceActive : boardWorkforceActive;
  // Ensure adminEmails is always an array
  const safeAdminEmails = Array.isArray(adminEmails) ? adminEmails : [];
  const safeMemberCount = memberCount || safeAdminEmails.length || 0;
  const [isWorkforceLoading, setIsWorkforceLoading] = useState(false);
  const boardAdmins = safeAdminEmails?.slice(0, 3);
  const remainingCount = Math.max(0, safeMemberCount - 3);
  // Use the board actions hook with permissions
  const {
    handleMenuAction,
    handleOpenWorkspace,
    handleWorkforceToggle,
    menuPermissions,
    isBoardSelected
  } = useBoardCardActions(board);

  // Always use the current board data for workforce state
  const isWorkforceActive = isActive;

  const handleWorkforceActiveChange = async (checked) => {
    setIsWorkforceLoading(true);
    try {
      await handleWorkforceToggle(checked);
      onWorkforceActiveChange?.(board.id, checked);
    } catch (error) {
      // Error is already handled in the hook with toast
    } finally {
      setIsWorkforceLoading(false);
    }
  };

  const isSelectedBoard = isBoardSelected(board.id);

  return (
    <TooltipProvider>
      <Card
        className={`transition-all duration-300 ease-in-out h-full hover:scale-[1.02] ${
        isSelectedBoard ? 'border-primary border' : board.deleteDate ? 'border-destructive border' : 'border-border'
        } bg-card shadow-sm hover:shadow-lg`}
      >
        <CardContent className="p-4 flex flex-col h-full">
          {/* Header */}
          <div className="flex justify-between items-start mb-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-4 mb-4">
                <h3 className={`text-swivu-md font-semibold overflow-hidden text-ellipsis whitespace-nowrap ${
                  board.deleteDate ? 'text-destructive' : 'text-foreground'
                }`}>
                  {displayName}
                </h3>
              </div>

              {/* Workforce Toggle */}
              <div className="flex items-center gap-swivu-base">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={isWorkforceLoading}
                      className={`h-8 w-8 p-0 rounded-full transition-none ${
                        isWorkforceActive
                          ? "bg-primary/10 text-primary"
                          : "bg-muted text-muted-foreground"
                      } ${isWorkforceLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                      onClick={() => handleWorkforceActiveChange(!isWorkforceActive)}
                      aria-label={isWorkforceActive ? "Deactivate Workforce" : "Activate Workforce"}
                    >
                      {isWorkforceLoading ? (
                        <div className="animate-spin rounded-full h-3 w-3 border-b border-current"></div>
                      ) : (
                        <i className="fa fa-briefcase text-"></i>
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-sm">
                      {isWorkforceLoading
                        ? "Updating..."
                        : isWorkforceActive
                          ? "Workforce is Active"
                          : "Workforce is Inactive"
                      }
                    </p>
                  </TooltipContent>
                </Tooltip>
                <span className="text-swivu-sm font-medium text-muted-foreground">
                  {isWorkforceLoading ? "Updating..." : isWorkforceActive ? "Workforce Active" : "Workforce Inactive"}
                </span>
              </div>
            </div>

            {/* Updated Dropdown Menu with Permissions */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground hover:bg-muted"
                >
                  <i className="fa fa-ellipsis-h text-sm"></i>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-card border-border shadow-lg">
                {/* Rename Board */}
                <DropdownMenuItem
                  onClick={() => handleMenuAction('rename')}
                  disabled={!menuPermissions.canRename}
                  className={`${
                    menuPermissions.canRename
                      ? 'text-foreground hover:bg-muted'
                      : 'text-muted-foreground cursor-not-allowed opacity-50'
                  }`}
                >
                  <i className="fa-regular fa-edit mr-2"></i>
                  Rename Board
                </DropdownMenuItem>

                {/* Duplicate Board */}
                <DropdownMenuItem
                  onClick={() => handleMenuAction('duplicate')}
                  disabled={!menuPermissions.canDuplicate}
                  className={`${
                    menuPermissions.canDuplicate
                      ? 'text-foreground hover:bg-muted'
                      : 'text-muted-foreground cursor-not-allowed opacity-50'
                  }`}
                >
                  <i className="fa-regular fa-copy mr-2"></i>
                  Duplicate Board
                </DropdownMenuItem>

                {/* Delete Board - Only show if not deleted */}
                {menuPermissions.showDelete && (
                  <DropdownMenuItem
                    onClick={() => handleMenuAction('delete')}
                    disabled={!menuPermissions.canDelete}
                    className={`${
                      menuPermissions.canDelete
                        ? 'text-destructive hover:bg-destructive/10'
                        : 'text-muted-foreground cursor-not-allowed opacity-50'
                    }`}
                  >
                    <i className="fa-regular fa-trash mr-2"></i>
                    Delete Board
                  </DropdownMenuItem>
                )}

                {/* Cancel Delete - Only show if deleted */}
                {menuPermissions.showCancelDelete && (
                  <DropdownMenuItem
                    onClick={() => handleMenuAction('cancelDelete')}
                    disabled={!menuPermissions.canCancelDelete}
                    className={`${
                      menuPermissions.canCancelDelete
                        ? 'text-primary hover:bg-primary/10'
                        : 'text-muted-foreground cursor-not-allowed opacity-50'
                    }`}
                  >
                    <i className="fa-regular fa-undo mr-2"></i>
                    Cancel Delete
                  </DropdownMenuItem>
                )}

                {/* Leave Board - Always available */}
                <DropdownMenuItem
                  onClick={() => handleMenuAction('leave')}
                  disabled={!menuPermissions.canLeave}
                  className={`${
                    menuPermissions.canLeave
                      ? 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                      : 'text-gray-400 dark:text-gray-500 cursor-not-allowed opacity-50'
                  }`}
                >
                  <i className="fa-regular fa-sign-out-alt mr-2"></i>
                  Leave Board
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Members & Role */}
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-swivu-horizontal">
              <div className="flex -space-x-2">
                {boardAdmins && boardAdmins.map((admin, index) => {
                  const adminEmail = typeof admin === 'string' ? admin : admin.email;
                  const adminInitial = adminEmail ? adminEmail.slice(0, 1).toUpperCase() : '?';
                  const adminImage = typeof admin === 'object' ? admin.image_url : null;
                  return (
                    <Tooltip key={index}>
                      <TooltipTrigger asChild>
                        <Avatar className="w-8 h-8 border-2 border-background shadow-sm">
                          <AvatarImage src={adminImage || "/placeholder.svg"} alt={adminEmail} />
                          <AvatarFallback className="text-xs font-medium bg-[#3f61e9] text-white">
                            {adminInitial}
                          </AvatarFallback>
                        </Avatar>
                      </TooltipTrigger>
                      <TooltipContent className="bg-gray-900 dark:bg-gray-800 text-white dark:text-gray-200">
                        <div className="text-sm">{adminEmail}</div>
                      </TooltipContent>
                    </Tooltip>
                  );
                })}
                {remainingCount > 0 && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="w-8 h-8 rounded-full bg-muted border-2 border-background shadow-sm flex items-center justify-center">
                        <span className="text-xs font-medium text-muted-foreground">+{remainingCount}</span>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent className="bg-popover text-popover-foreground">
                      <div className="flex flex-col gap-1">
                        {safeAdminEmails && safeAdminEmails.slice(3, 8).map((admin, index) => {
                          const adminEmail = typeof admin === 'string' ? admin : admin.email;
                          return (
                            <div key={index} className="text-sm">{adminEmail}</div>
                          );
                        })}
                        {safeMemberCount > 8 && <div className="text-xs text-muted-foreground">+{safeMemberCount - 8} more</div>}
                      </div>
                    </TooltipContent>
                  </Tooltip>
                )}
              </div>

              <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                <i className="fa fa-users"></i>
                <span className="font-medium">{safeMemberCount}</span>
              </div>
            </div>

            <Badge variant="outline" className="text-xs font-medium border-border text-foreground bg-muted">
              {role}
            </Badge>
          </div>

          {/* Metadata */}
          <div className="grid grid-cols-[auto_auto] mb-4">
            <div className="flex items-center gap-4 text-sm text-muted-foreground w-100">
              <i className="fa fa-clock text-base"></i>
              <div>
                <div className="text-swivu-sm font-medium text-foreground">Updated {lastUpdatedAt} ago</div>
                <div className="text-xs text-muted-foreground">by {updatedBy}</div>
              </div>
            </div>

            <div className="flex items-center gap-4 text-muted-foreground ml-auto">
              <i className="fa fa-calendar-alt text-base"></i>
              <div>
                <div className="text-swivu-sm font-medium text-foreground">Created</div>
                <div className="text-xs text-muted-foreground">{age} ago</div>
              </div>
            </div>
          </div>

          <Button
            size="sm"
            variant="outline"
            className="bg-background/50"
            onClick={() => {
              onBoardAccess?.(board.id);
              handleOpenWorkspace();
            }}
          >
            Open Board
          </Button>
        </CardContent>
      </Card>
    </TooltipProvider>
  );
}
